// src/lib/supabase.js
// ─────────────────────────────────────────────
// Supabase client initialisation
// Replace REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY
// in your .env file with your actual Supabase project values.
// ─────────────────────────────────────────────

import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables. Check your .env file.');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
  },
});

// ─────────────────────────────────────────────
// DATABASE SCHEMA — run these SQL statements in
// your Supabase SQL editor (Project → SQL Editor)
// ─────────────────────────────────────────────
/*


-- ════════════════════════════════════════════════════════════
-- IMPORTANT: RUN THIS IN SUPABASE SQL EDITOR FIRST
-- This fixes the RLS infinite recursion bug that prevents
-- admin role from being detected. Run this even if you already
-- ran the full schema below.
-- ════════════════════════════════════════════════════════════

-- 1. Drop the old recursive policies
drop policy if exists "Admins can view all profiles"  on public.profiles;
drop policy if exists "Admins can update all profiles" on public.profiles;
drop policy if exists "Admins can manage products"     on public.products;
drop policy if exists "Admins can manage categories"   on public.categories;
drop policy if exists "Admins can manage orders"       on public.orders;
drop policy if exists "Admins can manage blog"         on public.blog_posts;
drop policy if exists "Admins can manage reviews"      on public.product_reviews;
drop policy if exists "Admins can manage newsletter"   on public.newsletter_subscribers;

-- 2. Create a SECURITY DEFINER helper function
--    This reads the role WITHOUT triggering policies (bypasses recursion)
create or replace function public.get_my_role()
returns text
language sql
stable
security definer
set search_path = public
as $$
  select role from public.profiles where id = auth.uid();
$$;

-- 3. Re-create all admin policies using the helper function
create policy "Admins can manage products" on public.products for all
  using (public.get_my_role() in ('admin','staff'));

create policy "Admins can manage categories" on public.categories for all
  using (public.get_my_role() in ('admin','staff'));

create policy "Admins can manage orders" on public.orders for all
  using (public.get_my_role() in ('admin','staff'));

create policy "Admins can manage blog" on public.blog_posts for all
  using (public.get_my_role() in ('admin','staff'));

create policy "Admins can manage reviews" on public.product_reviews for all
  using (public.get_my_role() in ('admin','staff'));

create policy "Admins can view all profiles" on public.profiles for select
  using (public.get_my_role() in ('admin','staff'));

create policy "Admins can update profiles" on public.profiles for update
  using (public.get_my_role() = 'admin');

-- 4. Make sure your account is admin
-- Replace the email below with your actual email:
-- update public.profiles set role = 'admin' where email = 'your@email.com';

-- ════════════════════════════════════════════════════════════
-- END OF RLS FIX — run the full schema below only on a fresh project
-- ════════════════════════════════════════════════════════════

-- EXTENSIONS
create extension if not exists "uuid-ossp";

-- PROFILES (extends Supabase auth.users)
create table public.profiles (
  id uuid references auth.users(id) on delete cascade primary key,
  email text not null,
  full_name text,
  phone text,
  role text not null default 'customer' check (role in ('customer', 'staff', 'admin')),
  loyalty_points integer not null default 0,
  loyalty_tier text not null default 'stone' check (loyalty_tier in ('stone', 'crystal', 'elite')),
  preferred_language text not null default 'en',
  preferred_country text not null default 'DE',
  wishlist uuid[] default '{}',
  age_verified boolean not null default false,
  gdpr_consent boolean not null default false,
  marketing_consent boolean not null default false,
  created_at timestamptz not null default now(),
  updated_at timestamptz not null default now()
);

-- Auto-create profile on signup
create or replace function public.handle_new_user()
returns trigger language plpgsql security definer set search_path = public
as $$
begin
  insert into public.profiles (id, email, full_name)
  values (
    new.id,
    new.email,
    coalesce(new.raw_user_meta_data->>'full_name', split_part(new.email, '@', 1))
  );
  return new;
end;
$$;

create trigger on_auth_user_created
  after insert on auth.users
  for each row execute procedure public.handle_new_user();

-- Auto-update loyalty tier when points change
create or replace function public.update_loyalty_tier()
returns trigger language plpgsql as $$
begin
  new.loyalty_tier := case
    when new.loyalty_points >= 2000 then 'elite'
    when new.loyalty_points >= 500  then 'crystal'
    else 'stone'
  end;
  new.updated_at := now();
  return new;
end;
$$;

create trigger on_points_change
  before update of loyalty_points on public.profiles
  for each row execute procedure public.update_loyalty_tier();

-- CATEGORIES
create table public.categories (
  id uuid default uuid_generate_v4() primary key,
  slug text not null unique,
  name jsonb not null default '{}',
  icon text not null default '🌿',
  sort_order integer not null default 0,
  active boolean not null default true,
  created_at timestamptz not null default now()
);

-- PRODUCTS
create table public.products (
  id uuid default uuid_generate_v4() primary key,
  category_id uuid references public.categories(id),
  slug text not null unique,
  name jsonb not null default '{}',
  description jsonb not null default '{}',
  tags jsonb not null default '[]',
  images text[] not null default '{}',
  price_eur numeric(10,2) not null check (price_eur > 0),
  price_eur_original numeric(10,2),
  stock integer not null default 0 check (stock >= 0),
  sku text,
  weight_grams integer,
  thc_percent numeric(4,2) not null default 0.00,
  cbd_percent numeric(4,2),
  origin text,
  badge text,
  active boolean not null default true,
  featured boolean not null default false,
  is_new boolean not null default false,
  sort_order integer not null default 0,
  created_at timestamptz not null default now(),
  updated_at timestamptz not null default now()
);

-- ORDERS
create table public.orders (
  id uuid default uuid_generate_v4() primary key,
  user_id uuid references public.profiles(id),
  mollie_payment_id text unique,
  mollie_order_id text unique,
  status text not null default 'pending'
    check (status in ('pending','paid','processing','shipped','delivered','cancelled','refunded')),
  items jsonb not null default '[]',
  subtotal_eur numeric(10,2) not null,
  shipping_eur numeric(10,2) not null default 4.90,
  total_eur numeric(10,2) not null,
  -- CRITICAL: always stored and charged in EUR
  -- display_currency is cosmetic only and NEVER used for payment
  display_currency text not null default 'EUR',
  shipping_address jsonb not null default '{}',
  billing_address jsonb,
  customer_email text not null,
  customer_name text not null,
  loyalty_points_earned integer not null default 0,
  loyalty_points_used integer not null default 0,
  discount_code text,
  discount_eur numeric(10,2) not null default 0,
  notes text,
  collector_consent boolean not null default false,
  age_verified boolean not null default false,
  created_at timestamptz not null default now(),
  updated_at timestamptz not null default now()
);

-- ORDER STATUS LOG
create table public.order_status_log (
  id uuid default uuid_generate_v4() primary key,
  order_id uuid references public.orders(id) on delete cascade,
  status text not null,
  note text,
  created_at timestamptz not null default now()
);

-- DISCOUNT CODES
create table public.discount_codes (
  id uuid default uuid_generate_v4() primary key,
  code text not null unique,
  type text not null check (type in ('percent', 'fixed')),
  value numeric(10,2) not null,
  min_order_eur numeric(10,2) not null default 0,
  max_uses integer,
  uses integer not null default 0,
  active boolean not null default true,
  expires_at timestamptz,
  created_at timestamptz not null default now()
);

-- BLOG POSTS
create table public.blog_posts (
  id uuid default uuid_generate_v4() primary key,
  slug text not null unique,
  title jsonb not null default '{}',
  excerpt jsonb not null default '{}',
  content jsonb not null default '{}',
  category text not null default 'general',
  cover_image text,
  published boolean not null default false,
  featured boolean not null default false,
  author_id uuid references public.profiles(id),
  read_time_minutes integer not null default 5,
  created_at timestamptz not null default now(),
  updated_at timestamptz not null default now()
);

-- NEWSLETTER SUBSCRIBERS
create table public.newsletter_subscribers (
  id uuid default uuid_generate_v4() primary key,
  email text not null unique,
  name text,
  active boolean not null default true,
  language text not null default 'en',
  country text not null default 'DE',
  subscribed_at timestamptz not null default now()
);

-- PUSH NOTIFICATIONS LOG
create table public.push_notifications (
  id uuid default uuid_generate_v4() primary key,
  title text not null,
  body text not null,
  sent_to text not null default 'all',
  sent_at timestamptz not null default now()
);

-- PRODUCT REVIEWS
create table public.product_reviews (
  id uuid default uuid_generate_v4() primary key,
  product_id uuid references public.products(id) on delete cascade,
  user_id uuid references public.profiles(id),
  rating integer not null check (rating between 1 and 5),
  title text,
  body text,
  verified_purchase boolean not null default false,
  approved boolean not null default false,
  created_at timestamptz not null default now()
);

-- ROW LEVEL SECURITY ─────────────────────────────────
alter table public.profiles enable row level security;
alter table public.products enable row level security;
alter table public.categories enable row level security;
alter table public.orders enable row level security;
alter table public.blog_posts enable row level security;
alter table public.newsletter_subscribers enable row level security;
alter table public.product_reviews enable row level security;

-- Profiles: users can only read/update their own
create policy "Users can view own profile" on public.profiles for select using (auth.uid() = id);
create policy "Users can update own profile" on public.profiles for update using (auth.uid() = id);
-- NOTE: we do NOT add a separate "Admins can view all profiles" policy because
-- querying profiles to check role inside a profiles policy creates infinite recursion.
-- Instead, the "users can view own profile" policy above is enough for the app to
-- read profile.role — each user fetches only their own profile row.
-- Admin operations that need to read all profiles use the service role key in
-- serverless functions, not the anon key in the browser.

-- Products: anyone can read active products, only admins can write
create policy "Anyone can view active products" on public.products for select using (active = true);
create policy "Admins can manage products" on public.products for all using (
  (select role from public.profiles where id = auth.uid()) in ('admin','staff')
);

-- Categories: public read, admin write
create policy "Anyone can view categories" on public.categories for select using (active = true);
create policy "Admins can manage categories" on public.categories for all using (
  (select role from public.profiles where id = auth.uid()) in ('admin','staff')
);

-- Orders: users see own orders, admins see all
create policy "Users can view own orders" on public.orders for select using (user_id = auth.uid());
create policy "Users can create orders" on public.orders for insert with check (user_id = auth.uid());
create policy "Admins can manage orders" on public.orders for all using (
  (select role from public.profiles where id = auth.uid()) in ('admin','staff')
);

-- Blog: published posts are public, admins manage all
create policy "Anyone can view published posts" on public.blog_posts for select using (published = true);
create policy "Admins can manage blog" on public.blog_posts for all using (
  (select role from public.profiles where id = auth.uid()) in ('admin','staff')
);

-- Reviews: approved reviews are public
create policy "Anyone can view approved reviews" on public.product_reviews for select using (approved = true);
create policy "Users can submit reviews" on public.product_reviews for insert with check (auth.uid() = user_id);
create policy "Admins can manage reviews" on public.product_reviews for all using (
  (select role from public.profiles where id = auth.uid()) in ('admin','staff')
);

-- INITIAL CATEGORIES
insert into public.categories (slug, name, icon, sort_order) values
  ('new-arrivals', '{"en":"New Arrivals","de":"Neuheiten","sk":"Novinky","hu":"Újdonságok","ro":"Noutăți","pl":"Nowości","hr":"Novosti","nl":"Nieuw","at":"Neuheiten"}', '⭐', 0),
  ('cbd-flower',   '{"en":"CBD Flower","de":"CBD Blüten","sk":"CBD Kvet","hu":"CBD Virág","ro":"CBD Floare","pl":"CBD Kwiat","hr":"CBD Cvijet","nl":"CBD Bloem","at":"CBD Blüten"}', '🌿', 1),
  ('hemp-resin',   '{"en":"Hemp Resin","de":"Hanfharz","sk":"Konopná Živica","hu":"Kendő Gyanta","ro":"Rășină Cânepă","pl":"Żywica Konopna","hr":"Konopljina Smola","nl":"Henneprésine","at":"Hanfharz"}', '🧱', 2),
  ('vapes-carts',  '{"en":"Vapes & Carts","de":"Vapes & Carts","sk":"Vapes & Carts","hu":"Vapes & Carts","ro":"Vape & Cart","pl":"Vape i Kartridże","hr":"Vapes i Uložaci","nl":"Vapes & Carts","at":"Vapes & Carts"}', '💨', 3),
  ('bundles',      '{"en":"Bundles","de":"Pakete","sk":"Balíčky","hu":"Csomagok","ro":"Pachete","pl":"Zestawy","hr":"Paketi","nl":"Bundels","at":"Pakete"}', '🎁', 4);

-- SEED ADMIN USER (run AFTER creating your account)
-- Replace 'your-user-id' with your actual Supabase auth user ID
-- update public.profiles set role = 'admin' where id = 'your-user-id';

*/
